import {
  collection,
  doc,
  getDoc,
  updateDoc,
  type CollectionReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { groupDocumentConverter } from "@kekkle/shared/convertors/web/group-document.converter";
import type { GroupDocument } from "@kekkle/shared/types/documents/group-document/group-document";
import { handleFirestoreException } from "@/helpers/exceptions/handler";

/**
 * Return the groups collection reference with converter
 */
function groupCollection(): CollectionReference<GroupDocument> {
  return collection(firestore, "groups").withConverter(groupDocumentConverter);
}

/**
 * Fetches groups based on the provided IDs
 * @param groupIds - Array of group IDs
 * @returns Array of group documents
 */
export async function fetchGroupsById(
  groupIds: string[],
): Promise<GroupDocument[]> {
  const groups: GroupDocument[] = [];
  for (const groupId of groupIds) {
    const groupDoc = await fetchGroupById(groupId);
    if (groupDoc) {
      groups.push(groupDoc);
    }
  }
  return groups;
}

/**
 * Fetches a group by its ID
 * @param groupId - Group ID
 * @returns Group document or null if not found
 */
export async function fetchGroupById(
  groupId: string,
): Promise<GroupDocument | null> {
  const groupDoc = await getDoc(doc(groupCollection(), groupId));
  if (!groupDoc.exists()) {
    return null;
  }
  return groupDoc.data();
}

/**
 * Updates a group's basic information
 * @param groupId - Group ID
 * @param updates - Partial group data to update
 */
export async function updateGroup(
  groupId: string,
  updates: Partial<Pick<GroupDocument, "name" | "description">>,
): Promise<void> {
  try {
    const groupRef = doc(groupCollection(), groupId);
    await updateDoc(groupRef, updates);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Updates a group's picture information
 * @param groupId - Group ID
 * @param groupPicturePath - Path to the picture in storage
 * @param groupPictureBlurHash - BlurHash string for the picture
 */
export async function updateGroupPicture(
  groupId: string,
  groupPicturePath: string,
  groupPictureBlurHash: string,
): Promise<void> {
  try {
    const groupRef = doc(groupCollection(), groupId);
    await updateDoc(groupRef, {
      group_picture: groupPicturePath,
      group_picture_blurhash: groupPictureBlurHash,
    });
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Updates a user's birthday across multiple groups
 * @param userId - User's UUID
 * @param groupIds - Array of group IDs to update
 * @param birthday - New birthday (null to clear)
 * @returns Object with success status and any failed group IDs
 */
export async function updateUserBirthdayInGroups(
  userId: string,
  groupIds: string[],
  birthday: Date | null,
): Promise<{ success: boolean; failedGroups: string[] }> {
  const failedGroups: string[] = [];

  for (const groupId of groupIds) {
    try {
      const groupRef = doc(groupCollection(), groupId);
      await updateDoc(groupRef, {
        [`members.${userId}.birthday`]: birthday,
      });
    } catch (e) {
      handleFirestoreException({ e: e as FirestoreError });
      failedGroups.push(groupId);
    }
  }

  return {
    success: failedGroups.length === 0,
    failedGroups,
  };
}
